/**
 * WMS Marketing — public showcase of the Warehouse Management System.
 *
 * Two tiers:
 *   - Public (always visible): hero, module feature cards, benefits,
 *     Contact Us form.
 *   - Deeper detail (gated by `wms.view_detailed`): workflow diagrams
 *     + full per-module breakdowns.
 *
 * Design: dark hero + accent amber CTAs, Inter typography, card-based
 * module grid with lucide icons. Contact form posts to
 * POST /api/wms/contact — the backend saves to the DB AND emails
 * qayum2b@gmail.com from info@smrtin.net.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Boxes, Truck, PackageOpen, Package, Users, ScanLine,
  Layers, GitBranch, ShieldCheck, Activity, Workflow, FileBarChart2,
  Smartphone, ArrowRight, CheckCircle2, Mail, Phone, Building2,
  MessageSquare, User as UserIcon, Loader2, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --------------------------------------------------------------------
// Static content — modules shown as feature cards on the public page.
// Keep this list tight (12 cards) so the grid stays readable at every
// breakpoint. The icons are lucide-react so we don't pull a new asset
// package. `detail` is only rendered inside the deeper-detail tier.
// --------------------------------------------------------------------
const MODULES = [
  {
    key: 'receiving',
    icon: PackageOpen,
    title: 'Goods Receiving / ASN',
    blurb: 'Capture pallet-level receipts, scan barcodes, and hold inventory for QC before it goes orderable.',
    detail: [
      'ASN release-hold workflow with scan-fallback for missing expiry / production dates.',
      'Bulk Excel paste or line-by-line entry; both routes land on the same put-away queue.',
      'Branch-split receipts (plastic / wooden crates separated at receive time).',
    ],
  },
  {
    key: 'putaway',
    icon: Package,
    title: 'Put-Away',
    blurb: 'Spawn put-away tasks per received line and route pickers to the right rack bay.',
    detail: [
      'Idempotent task generation with a composite unique index — no duplicates.',
      'Multi-picker split assignment: supervisors evenly divide locations alphabetically.',
      'Inline per-row picker reassignment without a page refresh.',
    ],
  },
  {
    key: 'picking',
    icon: ScanLine,
    title: 'Order Picking',
    blurb: 'FIFO or FEFO allocation per category, live pick status, shortfall cascade.',
    detail: [
      'Dispatch method configurable per Category (FEFO for food, FIFO for packaging).',
      'Shortfall cascade re-plans a short pick onto the next pallet idempotently.',
      'Pick list PDF + Excel exports with alphabetical location ordering.',
    ],
  },
  {
    key: 'loading',
    icon: Truck,
    title: 'Loading & Dispatch',
    blurb: 'Pre-assign loaders at allocation time, print the loading sheet, confirm dispatch.',
    detail: [
      'Loading tasks spawn alongside picking so supervisors see them day-one.',
      'Dedicated loading sheet PDF with no-store cache so adjustments show instantly.',
      'Driver dispatch tracker with multi-stop OSRM route optimization.',
    ],
  },
  {
    key: 'floor',
    icon: Smartphone,
    title: 'Real-Time Floor App',
    blurb: 'Pickers see assignments the moment they land — no polling delays, no stale lists.',
    detail: [
      'Server-sent-events channel per picker — reassignments and status flips are instant.',
      'Scan-first UX, location shown natively on every task card.',
      '20s poll remains as a reconnection safety net.',
    ],
  },
  {
    key: 'rbac',
    icon: ShieldCheck,
    title: 'Role-Based Access',
    blurb: 'Granular per-module permissions, category scopes, and warehouse-scoped admins.',
    detail: [
      'Super-admin-grantable slugs for every sensitive action.',
      'Category-slug scoping restricts a picker to the SKUs they own.',
      'Audit log on every mutation (reassignment, status flip, intent write).',
    ],
  },
  {
    key: 'multi-picker',
    icon: Users,
    title: 'Multi-Picker Split',
    blurb: 'Divide a wave across N pickers in one click — tasks split by alphabetical location.',
    detail: [
      'Write-verified: every targeted task is re-read after update to confirm the picker landed.',
      '`skipped_task_ids` list surfaces any tasks that fell out mid-write.',
      'Bells only fire when a block actually got rows persisted.',
    ],
  },
  {
    key: 'batch',
    icon: Layers,
    title: 'Batch-Level Inventory',
    blurb: 'Track production + expiry per pallet; FEFO automatically favours the batch closest to expiry.',
    detail: [
      'Ledger-master paradigm — `stock_events` is the immutable truth.',
      'Placements collection tracks physical floor state and allocations.',
      'Hold column + release flow for inventory under QC.',
    ],
  },
  {
    key: 'warehouse-mgmt',
    icon: Boxes,
    title: 'Warehouse & Zones',
    blurb: 'Multiple warehouses, zoned bays, floor-incharge scopes, and category restrictions.',
    detail: [
      'Zone-level task scoping for floor supervisors.',
      'Branch FEFO warehouse system with isolated receipts.',
      'MyGate-style analytics dashboard across every warehouse.',
    ],
  },
  {
    key: 'reports',
    icon: FileBarChart2,
    title: 'Reports & Audit',
    blurb: 'Live stock, Outstanding, Day Close, Allocation Details, barcode slips — all one click.',
    detail: [
      'PDF + Excel exports on every operational list.',
      'Day Close orchestrates stock + fulfilment + adjustments by date.',
      'Cross-order Allocation Details report pinpoints every placement a SKU is reserved on.',
    ],
  },
  {
    key: 'barcode',
    icon: Activity,
    title: 'Barcode Everywhere',
    blurb: 'Pallet labels, task scans, invoice scans, product scans — one scanner across the ops stack.',
    detail: [
      'Advanced label printing with per-category templates.',
      'Barcode invoice scanning accelerates loading reconciliation.',
      'Floor App Stock Check pulls live counts from any location in seconds.',
    ],
  },
  {
    key: 'integrations',
    icon: GitBranch,
    title: 'Integrations',
    blurb: 'D7 SMS, WhatsApp broadcasts, web push, Brevo email, Google Drive docs, and route APIs.',
    detail: [
      'SMS OTP login + WhatsApp broadcasting for marketing + ops.',
      'Web push notifications on task assignment, back-grounded with 10s timeout.',
      'OSRM for route optimization; Google Drive for document archival.',
    ],
  },
];

const BENEFITS = [
  { title: 'Zero stock surprises', body: 'Ledger-master + placements split keeps the storefront, floor, and reports in exact lock-step.' },
  { title: 'Instant floor feedback', body: 'SSE channel pushes reassignments and status flips to the handheld the moment they commit.' },
  { title: 'Audit everything', body: 'Every write — reassignment, intent change, stock adjust — leaves a timestamped trail.' },
  { title: 'Fast to roll out', body: 'Admin-configurable categories, warehouses, and permissions let you launch in days, not months.' },
];

// --------------------------------------------------------------------
// SVG workflow diagram — Receive → Put-away → Pick → Load → Dispatch.
// Rendered inside the deeper-detail tier.
// --------------------------------------------------------------------
const WorkflowDiagram = () => {
  const steps = [
    { label: 'Receive', sub: 'ASN scan', color: '#0EA5E9' },
    { label: 'Put-away', sub: 'to rack', color: '#8B5CF6' },
    { label: 'Pick', sub: 'FEFO / FIFO', color: '#F59E0B' },
    { label: 'Load', sub: 'crate + verify', color: '#EC4899' },
    { label: 'Dispatch', sub: 'OSRM route', color: '#10B981' },
  ];
  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox="0 0 1000 180"
        className="w-full min-w-[720px] h-auto"
        role="img"
        aria-label="Receive to Dispatch workflow diagram"
        data-testid="wms-workflow-svg"
      >
        {steps.map((s, i) => {
          const x = 60 + i * 190;
          return (
            <g key={s.label}>
              <rect
                x={x} y={48} width={140} height={84} rx={14}
                fill="#0F172A" stroke={s.color} strokeWidth={2}
              />
              <circle cx={x + 24} cy={74} r={10} fill={s.color} />
              <text x={x + 46} y={78} fill="#E2E8F0" fontSize={16} fontWeight="700">{s.label}</text>
              <text x={x + 24} y={112} fill="#94A3B8" fontSize={11}>{s.sub}</text>
              {i < steps.length - 1 && (
                <g>
                  <line
                    x1={x + 140} y1={90} x2={x + 180} y2={90}
                    stroke="#475569" strokeWidth={2}
                  />
                  <polygon
                    points={`${x + 180},86 ${x + 190},90 ${x + 180},94`}
                    fill="#475569"
                  />
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// --------------------------------------------------------------------
// Contact Us form
// --------------------------------------------------------------------
const ContactForm = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // confirmation text
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/wms/contact`, form);
      setDone(r.data?.confirmation || 'Thanks — we will reach out shortly.');
      setForm({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(
        Array.isArray(d) ? d.map((x) => x.msg).join(' · ') : d || 'Could not send — please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-4"
        data-testid="wms-contact-success"
      >
        <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0 mt-0.5" />
        <div>
          <div className="text-emerald-900 font-semibold text-base">Message received</div>
          <div className="mt-1 text-emerald-800 text-sm">{done}</div>
          <button
            type="button"
            onClick={() => setDone(null)}
            className="mt-4 text-emerald-700 hover:text-emerald-900 text-xs font-semibold underline"
            data-testid="wms-contact-send-another"
          >
            Send another enquiry
          </button>
        </div>
      </div>
    );
  }

  const Field = ({ icon: Icon, label, name, type = 'text', ...rest }) => (
    <label className="block">
      <span className="text-[12px] font-semibold tracking-wide text-neutral-600 uppercase">{label}</span>
      <div className="mt-1 relative">
        <Icon className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          {...rest}
          type={type}
          value={form[name]}
          onChange={update(name)}
          required
          className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
          data-testid={`wms-contact-${name}`}
        />
      </div>
    </label>
  );

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-6 sm:p-8"
      data-testid="wms-contact-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field icon={UserIcon}  label="Your name"     name="name"     placeholder="Jane Smith" />
        <Field icon={Building2} label="Company"       name="company"  placeholder="Acme Logistics" />
        <Field icon={Mail}      label="Email"         name="email"    type="email" placeholder="jane@acme.com" />
        <Field icon={Phone}     label="Phone"         name="phone"    placeholder="+971 50 123 4567" />
      </div>
      <label className="block mt-4">
        <span className="text-[12px] font-semibold tracking-wide text-neutral-600 uppercase">What would you like to discuss?</span>
        <div className="mt-1 relative">
          <MessageSquare className="w-4 h-4 text-neutral-400 absolute left-3 top-3" />
          <textarea
            rows={5}
            value={form.message}
            onChange={update('message')}
            required
            minLength={5}
            placeholder="Pricing, a live demo, specific module questions, integration needs…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-y"
            data-testid="wms-contact-message"
          />
        </div>
      </label>
      {error && (
        <div
          className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800"
          data-testid="wms-contact-error"
        >
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold text-sm px-6 py-3 shadow transition"
        data-testid="wms-contact-submit"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Send enquiry <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
      <p className="mt-3 text-[11px] text-neutral-500">
        We&apos;ll reply from <code className="px-1 bg-neutral-100 rounded">info@smrtin.net</code>.
        Your details are never shared with third parties.
      </p>
    </form>
  );
};

// --------------------------------------------------------------------
// Page shell
// --------------------------------------------------------------------
export default function WmsMarketing() {
  const [access, setAccess] = useState({ signed_in: false, can_view_detailed: false });
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem('admin_token') || localStorage.getItem('token') || '';
    const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
    axios.get(`${API}/wms/access`, { headers })
      .then((r) => setAccess(r.data || {}))
      .catch(() => setAccess({ signed_in: false, can_view_detailed: false }));
  }, []);

  const canDetail = !!access.can_view_detailed;
  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="bg-[#F8FAFC] text-[#0F172A] min-h-screen" data-testid="wms-marketing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden="true">
          <div className="absolute top-10 left-1/4 w-96 h-96 rounded-full bg-amber-400 blur-[120px]" />
          <div className="absolute bottom-0 right-10 w-80 h-80 rounded-full bg-sky-400 blur-[120px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-widest text-amber-300 font-semibold">
            <Workflow className="w-3.5 h-3.5" /> Warehouse Management System
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            The WMS your warehouse<br />
            <span className="text-amber-400">actually wants to use.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed">
            Receiving, put-away, picking, loading and dispatch — one tightly integrated stack with real-time
            floor tracking, multi-picker splits, FEFO / FIFO per category, and audit-grade RBAC.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={scrollTo('contact')}
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-neutral-900 font-bold text-sm px-6 py-3 shadow-lg shadow-amber-500/20 transition"
              data-testid="wms-hero-cta-contact"
            >
              Book a demo <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={scrollTo('modules')}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white font-semibold text-sm px-5 py-3 transition"
              data-testid="wms-hero-cta-modules"
            >
              Explore modules <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-10 flex items-center gap-6 text-xs text-neutral-400">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Live SSE floor updates</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Batch + FEFO native</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Granular RBAC</span>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="modules" className="max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-amber-600 font-bold">Modules</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
              Everything a modern warehouse needs — in one place.
            </h2>
          </div>
          <p className="max-w-md text-sm text-neutral-600">
            Twelve tightly integrated modules spanning receive, floor ops, dispatch, reporting, and compliance.
            Granular permissions let you roll any subset out to a specific team.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <div
                key={m.key}
                className="group relative rounded-2xl border border-neutral-200 bg-white p-6 hover:border-amber-300 hover:shadow-md transition"
                data-testid={`wms-module-${m.key}`}
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-neutral-900 text-amber-300 group-hover:bg-amber-500 group-hover:text-neutral-900 transition">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-[#0F172A]">{m.title}</h3>
                <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{m.blurb}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white border-y border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
          <div className="text-[11px] uppercase tracking-widest text-amber-600 font-bold">Why teams pick it</div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight max-w-2xl">
            Built for operators who need the ledger to tell the truth.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-xl border border-neutral-200 p-5" data-testid={`wms-benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <div className="mt-3 text-base font-bold">{b.title}</div>
                <div className="mt-1 text-sm text-neutral-600 leading-relaxed">{b.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEEPER DETAIL (gated) */}
      {canDetail && (
        <section
          className="bg-gradient-to-b from-[#0F172A] to-[#111827] text-white border-y border-black"
          data-testid="wms-deeper-detail-section"
        >
          <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-amber-400 font-bold">Deeper detail · restricted</div>
                <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
                  Workflows &amp; full module breakdown
                </h2>
                <p className="mt-2 max-w-xl text-sm text-neutral-300">
                  This section is only visible to admins granted the
                  <code className="mx-1 px-1 bg-white/10 rounded">wms.view_detailed</code>
                  permission.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDetail((v) => !v)}
                className="rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 text-xs font-semibold px-4 py-2 transition"
                data-testid="wms-detail-toggle"
              >
                {showDetail ? 'Hide' : 'Expand'} full breakdown
              </button>
            </div>

            <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
              <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">Primary workflow</div>
              <h3 className="mt-1 text-xl font-bold">Receive → Put-away → Pick → Load → Dispatch</h3>
              <div className="mt-6">
                <WorkflowDiagram />
              </div>
            </div>

            {showDetail && (
              <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
                {MODULES.map((m) => {
                  const Icon = m.icon;
                  return (
                    <div
                      key={m.key}
                      className="rounded-2xl border border-white/10 bg-white/5 p-6"
                      data-testid={`wms-module-detail-${m.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/15 text-amber-300">
                          <Icon className="w-5 h-5" />
                        </div>
                        <h4 className="text-base font-bold">{m.title}</h4>
                      </div>
                      <ul className="mt-4 space-y-2">
                        {m.detail.map((d, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-neutral-200">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="max-w-5xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-amber-600 font-bold">Contact us</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
              Pricing, specs, or a live demo?
            </h2>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              Tell us a bit about your operation and we&apos;ll get back to you with a tailored proposal.
              Most replies land within one business day.
            </p>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-center gap-3 text-neutral-700">
                <Mail className="w-4 h-4 text-amber-600" />
                <a href="mailto:info@smrtin.net" className="hover:underline">info@smrtin.net</a>
              </div>
              <div className="flex items-start gap-3 text-neutral-700">
                <Building2 className="w-4 h-4 text-amber-600 mt-0.5" />
                <span>Mohammed Riaz &amp; Partner LLC — WMS Division</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-500">
          <span>© {new Date().getFullYear()} Mohammed Riaz &amp; Partner LLC — WMS</span>
          <span>Built for warehouses that need the ledger to tell the truth.</span>
        </div>
      </footer>
    </main>
  );
}
